import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ErrorNotice, Layout, LoadingScreen, StatusPill } from '../components/Layout';
import { api } from '../lib/api';

interface ListItem {
  id: string;
  kind: 'appointment' | 'meeting';
  title: string | null;
  eventDate: string | null;
  eventAt: string | null;
  location: string | null;
  status: string;
  templateType: string | null;
  topicId: string | null;
}

interface ListPage {
  data: ListItem[];
  page: { hasMore: boolean; nextOffset: number | null };
}

function itemDate(item: ListItem) {
  const value = item.eventAt ?? item.eventDate;
  if (!value) return 'ยังไม่ระบุวันที่';
  const withTimezone = value.length === 10 ? `${value}T00:00:00+07:00`
    : /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}+07:00`;
  const parsed = new Date(withTimezone);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('th-TH', {
    timeZone: 'Asia/Bangkok', dateStyle: 'medium',
    ...(item.eventAt ? { timeStyle: 'short' as const } : {}),
  }).format(parsed);
}

export function AppointmentListPage() {
  const list = useInfiniteQuery({
    queryKey: ['all-items'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<ListPage>(`/api/items?limit=50&offset=${pageParam}`),
    getNextPageParam: (page) => page.page.nextOffset ?? undefined,
  });
  const items = list.data?.pages.flatMap((page) => page.data) ?? [];
  if (list.isLoading) return <Layout title="รายการทั้งหมด"><LoadingScreen /></Layout>;
  return (
    <Layout title="รายการทั้งหมด">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-slate-500">แสดง {items.length} รายการ · รวมนัดและการประชุม</p>
        <Link className="btn-primary" to="/create">+ สร้างรายการ</Link>
      </div>
      {list.isError && <ErrorNotice message={list.error.message} />}
      <section className="space-y-3">
        {items.map((item) => (
          <Link key={`${item.kind}-${item.id}`}
            to={item.kind === 'meeting' ? `/meeting-topics/${item.topicId}?meeting=${item.id}` : `/appointments/${item.id}`}
            className="card flex items-center justify-between gap-3 transition hover:border-teal-300">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-teal-700">{item.kind === 'meeting' ? 'การประชุม' : item.templateType === 'meeting' ? 'นัดประชุม' : 'รายการนัด'}</p>
              <h2 className="truncate font-semibold">{item.title ?? 'รายการใหม่'}</h2>
              <p className="mt-1 text-sm text-slate-600">{itemDate(item)}</p>
              <p className="truncate text-sm text-slate-500">{item.location ?? 'ยังไม่ระบุสถานที่'}</p>
            </div>
            <StatusPill status={item.status} />
          </Link>
        ))}
        {!items.length && !list.isError && <div className="card py-10 text-center text-slate-500">ยังไม่มีรายการ กด “สร้างรายการ” หรือนำเข้าจากกล่องนำเข้า</div>}
        {list.hasNextPage && <button className="btn-secondary w-full" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
          {list.isFetchingNextPage ? 'กำลังโหลด…' : 'โหลดรายการเก่าเพิ่ม'}
        </button>}
      </section>
    </Layout>
  );
}
